// NewCustomSkillModal.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  FaTimes,
  FaMagic,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSync,
  FaCheckCircle,
  FaHourglassHalf,
  FaArrowRight,
} from "react-icons/fa";
import styles from "./NewCustomSkillModal.module.css";

const MAX_BATCH_SIZE = 10;

/* ------------------------------------------------------------------ */
/*  Live countdown hook                                                */
/* ------------------------------------------------------------------ */
const useCountdown = (targetIso) => {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!targetIso) { setRemaining(0); return; }
    const tick = () => {
      const target = new Date(targetIso).getTime();
      const now = Date.now();
      const secs = Math.max(0, Math.floor((target - now) / 1000));
      setRemaining(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return { totalSeconds: remaining, mm, ss, isPast: remaining === 0 };
};

/* ------------------------------------------------------------------ */
/*  Modal                                                              */
/* ------------------------------------------------------------------ */
const NewCustomSkillModal = ({
  isOpen,
  onClose,
  appData,
  kbId,
  apiConfigs,
  chains,
  intentRecords,
  getAutobotUrl,
  onSaved,             // (draftRecord) → called per-slot after the batch completes, OR on close refresh
  onDraftsCreated,     // (batchId) → called immediately when the batch is scheduled
  onWireNow,           // (intentKey) → user clicks "Wire now" on a completed slot
}) => {
  /* step: "select" | "progress" */
  const [step, setStep] = useState("select");

  /* ---- Step 1 state ---- */
  const [selected, setSelected] = useState({});   // { [key]: true }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [rateLimited, setRateLimited] = useState(null); // { retry_after_seconds, message }

  /* ---- Step 2 state ---- */
  const [batchId, setBatchId] = useState(null);
  const [batch, setBatch] = useState(null);
  const [fetchingBatch, setFetchingBatch] = useState(false);
  const [batchError, setBatchError] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);

  /* countdowns */
  const retryCountdown = useCountdown(rateLimited?.retry_after_seconds ? new Date(Date.now() + rateLimited.retry_after_seconds * 1000).toISOString() : null);
  const startCountdown = useCountdown(scheduledAt);

  /* ---------- reset on open/close ---------- */
  useEffect(() => {
    if (!isOpen) {
      setStep("select");
      setSelected({});
      setSubmitting(false);
      setSubmitError(null);
      setRateLimited(null);
      setBatchId(null);
      setBatch(null);
      setFetchingBatch(false);
      setBatchError(null);
      setScheduledAt(null);
    }
  }, [isOpen]);

  /* ---------- selectable candidates: APIs + Chains ---------- */
  const candidates = useMemo(() => {
    const items = [];
    (apiConfigs || []).forEach((cfg) => {
      if (!cfg?.api_config) return;
      items.push({
        key: `api:${cfg.api_config_id}`,
        kind: "api",
        id: cfg.api_config_id,
        label: cfg.api_config?.url || cfg.api_config_id,
        method: cfg.api_config?.method || "?",
        api_config: cfg.api_config,
      });
    });
    (chains || []).forEach((chain) => {
      if (!chain?.root?.api_config_id) return;
      const rootCfg = (apiConfigs || []).find((c) => c.api_config_id === chain.root.api_config_id);
      if (!rootCfg?.api_config) return; // chain without a resolvable root is not suggestable
      items.push({
        key: `chain:${chain.chain_id}`,
        kind: "chain",
        id: chain.chain_id,
        label: chain.name || "Unnamed Chain",
        method: `CHAIN · ${(chain.steps || []).length} steps`,
        api_config: rootCfg.api_config,
      });
    });
    return items;
  }, [apiConfigs, chains]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleSelect = (key) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else {
        const count = Object.values(prev).filter(Boolean).length;
        if (count >= MAX_BATCH_SIZE) return prev; // hard cap
        next[key] = true;
      }
      return next;
    });
  };

  const canStartBatch = selectedCount > 0 && selectedCount <= MAX_BATCH_SIZE && !submitting;

  /* ---------- Step 1 submit ---------- */
  const startBatch = useCallback(async () => {
    if (!canStartBatch) return;
    setSubmitError(null);
    setRateLimited(null);
    setSubmitting(true);

    const token = localStorage.getItem("token");
    const meData = JSON.parse(localStorage.getItem("meData") || "{}");
    const tenantId = meData?.tenant_info?.id;
    const region = appData?.region?.toLowerCase() || "us";
    const industry = appData?.industry;
    const subIndustry = appData?.subIndustry;

    if (!tenantId || !kbId) {
      setSubmitError("Missing tenant or kb context. Please reload the page.");
      setSubmitting(false);
      return;
    }
    if (!industry) {
      setSubmitError("Missing industry. Please set your app's industry first.");
      setSubmitting(false);
      return;
    }

    const existingIntentKeys = (intentRecords || []).map((r) => r.intent_type).filter(Boolean);

    const items = Object.keys(selected)
      .filter((k) => selected[k])
      .map((k) => {
        const c = candidates.find((x) => x.key === k);
        return {
          api_config_id: c.kind === "api" ? c.id : null,
          chain_id: c.kind === "chain" ? c.id : null,
          api_config: c.api_config,
        };
      });

    try {
      const url = getAutobotUrl(`/ai_ingest_sys`, { task: "custom-intents/suggest-batch" });
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token || "" },
        body: JSON.stringify({
          tenant_id: tenantId,
          kb_id: kbId,
          region,
          industry,
          sub_industry: subIndustry,
          existing_intent_keys: existingIntentKeys,
          items,
        }),
      });

      const data = await resp.json();
      const inner = data?.body && typeof data.body === "object" ? data.body : data;

      if (!resp.ok) {
        setSubmitError(inner?.detail || inner?.message || `Batch start failed (HTTP ${resp.status})`);
        return;
      }

      if (inner.status === "rate_limited") {
        setRateLimited({
          retry_after_seconds: inner.retry_after_seconds || 86400,
          message: inner.message || "You've already run a suggestion batch in the last 24h.",
        });
        return;
      }
      if (inner.status === "unsupported") {
        setSubmitError(inner.message || "This industry/sub-industry is not yet supported.");
        return;
      }
      if (inner.status !== "pending" || !inner.batch_id) {
        setSubmitError(inner.message || "Unexpected response from batch endpoint.");
        return;
      }

      setBatchId(inner.batch_id);
      setScheduledAt(inner.scheduled_start_at);
      setStep("progress");

      if (typeof onDraftsCreated === "function") onDraftsCreated(inner.batch_id);

      /* First fetch immediately to seed the slot list (all pending). */
      refreshBatch(inner.batch_id);
    } catch (err) {
      setSubmitError(`Network error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }, [canStartBatch, selected, candidates, appData, kbId, intentRecords, getAutobotUrl, onDraftsCreated]);

  /* ---------- Step 2: refresh ---------- */
  const refreshBatch = useCallback(async (overrideId) => {
    const id = overrideId || batchId;
    if (!id) return;
    setFetchingBatch(true);
    setBatchError(null);
    try {
      const token = localStorage.getItem("token");
      const url = getAutobotUrl(`/ai_ingest_sys`, { task: `custom-intents/suggest-batch/${encodeURIComponent(id)}` });
      const resp = await fetch(url, { headers: { Authorization: token || "", "Content-Type": "application/json" } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      /* Batch GET is a passthrough from Lambda; no double-wrap. */
      const batchObj = data?.batch || data;
      setBatch(batchObj);
      if (batchObj?.scheduled_start_at) setScheduledAt(batchObj.scheduled_start_at);

      /* If the batch has finished, fire one onSaved() per completed slot
         so IntentIndex can add them to appLevelIntents without a page reload. */
      if (
        typeof onSaved === "function" &&
        ["completed", "partial", "failed"].includes(batchObj?.status) &&
        Array.isArray(batchObj?.intents)
      ) {
        batchObj.intents.forEach((slot) => {
          if (slot.suggestion_status === "completed") {
            onSaved({
              intent_key: slot.intent_key,
              display_name: slot.display_name,
              description: slot.description,
              examples: slot.examples || [],
              rules: slot.rules || [],
              is_app_level: true,
              suggestion_status: "completed",
              suggested_from_api_config_id: slot.suggested_from_api_config_id,
              suggested_from_chain_id: slot.suggested_from_chain_id,
              suggestion_batch_id: id,
            });
          }
        });
      }
    } catch (err) {
      setBatchError(err.message);
    } finally {
      setFetchingBatch(false);
    }
  }, [batchId, getAutobotUrl, onSaved]);

  /* Auto-refresh once when the scheduled start time arrives and the batch
     is still pending — gives the user feedback that something is happening
     without polling. */
  const hasAutoRefreshed = useRef(false);
  useEffect(() => {
    if (!startCountdown.isPast) return;
    if (hasAutoRefreshed.current) return;
    if (batch?.status && batch.status !== "pending") return;
    hasAutoRefreshed.current = true;
    refreshBatch();
  }, [startCountdown.isPast, batch, refreshBatch]);

  if (!isOpen) return null;

  const isBusy = submitting || (step === "progress" && fetchingBatch);

  return (
    <div className={styles.overlay} onClick={isBusy ? undefined : onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {step === "select" ? "Generate custom skill drafts" : "Suggestion batch progress"}
          </h2>
          <button className={styles.closeBtn} onClick={onClose} disabled={isBusy} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className={styles.body}>
          {/* ============================================================ */}
          {/* STEP 1 — SELECT + SUBMIT                                      */}
          {/* ============================================================ */}
          {step === "select" && (
            <>
              <p className={styles.intro}>
                Pick up to {MAX_BATCH_SIZE} APIs or Chains. The AI will draft an intent for each — name,
                description, examples, and rules. You can wire each one to its API later from the main
                screen. This run counts as your daily suggestion — choose carefully.
              </p>

              {rateLimited && (
                <div
                  style={{
                    margin: "12px 0",
                    padding: "12px 16px",
                    background: "#fef3c7",
                    border: "1px solid #fcd34d",
                    borderRadius: "6px",
                    color: "#92400e",
                    fontSize: "14px",
                  }}
                >
                  <strong>Suggestion quota used.</strong> {rateLimited.message}
                  <div style={{ marginTop: 6, fontFamily: "monospace", fontSize: "15px" }}>
                    Retry in {retryCountdown.mm}:{retryCountdown.ss}
                  </div>
                </div>
              )}

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  1. Select APIs / Chains ({selectedCount}/{MAX_BATCH_SIZE})
                </div>

                {candidates.length === 0 && (
                  <div className={styles.hint}>
                    No APIs or Chains available. Configure at least one in{" "}
                    <strong>Agent &amp; Servers → API Config</strong> first.
                  </div>
                )}

                <div
                  style={{
                    maxHeight: 340,
                    overflowY: "auto",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    padding: 4,
                    marginTop: 8,
                  }}
                >
                  {candidates.map((c) => {
                    const checked = !!selected[c.key];
                    const disabled = !checked && selectedCount >= MAX_BATCH_SIZE;
                    return (
                      <label
                        key={c.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.5 : 1,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled || submitting}
                          onChange={() => toggleSelect(c.key)}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: c.kind === "chain" ? "#dbeafe" : "#e0f2fe",
                            color: c.kind === "chain" ? "#1e40af" : "#075985",
                          }}
                        >
                          {c.method}
                        </span>
                        <span style={{ fontSize: 13, color: "#1e293b", wordBreak: "break-all" }}>
                          {c.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </section>

              {submitError && (
                <div className={styles.errorBox}>
                  <FaExclamationTriangle size={12} style={{ marginRight: 6 }} />
                  {submitError}
                </div>
              )}
            </>
          )}

          {/* ============================================================ */}
          {/* STEP 2 — PROGRESS                                             */}
          {/* ============================================================ */}
          {step === "progress" && (
            <>
              <p className={styles.intro}>
                Your batch is queued. You can close this window — the suggestions will keep generating in
                the background. Hit <strong>Refresh</strong> to check progress.
              </p>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "12px 16px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 2 }}>
                    {batch?.status === "pending" ? "Scheduled start" : "Batch status"}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                    {batch?.status === "pending" && !startCountdown.isPast && (
                      <>in {startCountdown.mm}:{startCountdown.ss}</>
                    )}
                    {batch?.status === "pending" && startCountdown.isPast && <>starting…</>}
                    {batch?.status === "in_progress" && <>Running · {batch.completed + batch.failed}/{batch.total}</>}
                    {batch?.status === "completed" && (
                      <span style={{ color: "#15803d" }}>
                        ✓ Completed · {batch.completed} succeeded
                      </span>
                    )}
                    {batch?.status === "partial" && (
                      <span style={{ color: "#a16207" }}>
                        ⚠ Partial · {batch.completed} ok / {batch.failed} failed
                      </span>
                    )}
                    {batch?.status === "failed" && (
                      <span style={{ color: "#b91c1c" }}>
                        ✗ Failed · {batch.failed} failed
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => refreshBatch()}
                  disabled={fetchingBatch}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    background: "#4a7feb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: fetchingBatch ? "not-allowed" : "pointer",
                    fontWeight: 500,
                    opacity: fetchingBatch ? 0.6 : 1,
                  }}
                >
                  <FaSync className={fetchingBatch ? "spin" : ""} /> {fetchingBatch ? "Refreshing…" : "Refresh"}
                </button>
              </div>

              {batchError && (
                <div className={styles.errorBox} style={{ marginBottom: 12 }}>
                  <FaExclamationTriangle size={12} style={{ marginRight: 6 }} />
                  Could not fetch batch: {batchError}
                </div>
              )}

              {batch?.intents && batch.intents.length > 0 ? (
                <div>
                  {batch.intents.map((slot) => (
                    <SlotRow key={slot.id || slot.suggestion_slot} slot={slot} onWireNow={onWireNow} />
                  ))}
                </div>
              ) : (
                <div style={{ padding: "20px 0", color: "#94a3b8", fontSize: 13, textAlign: "center" }}>
                  No slots yet. Hit <strong>Refresh</strong> to load them.
                </div>
              )}

              <div className={styles.hint} style={{ marginTop: 16 }}>
                When you close this window, the main screen will show these drafts. Click a draft to wire it
                to its API or Chain.
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={isBusy}>
            {step === "progress" ? "Close" : "Cancel"}
          </button>
          <div className={styles.footerRight}>
            {step === "select" && (
              <button
                className={styles.activateBtn}
                onClick={startBatch}
                disabled={!canStartBatch || !!rateLimited}
                title={
                  rateLimited
                    ? "Daily suggestion quota used — try again after the countdown."
                    : selectedCount === 0
                      ? "Select at least one API or Chain."
                      : `Generate ${selectedCount} draft${selectedCount === 1 ? "" : "s"}.`
                }
              >
                {submitting ? "Starting…" : `Generate ${selectedCount || ""} draft${selectedCount === 1 ? "" : "s"}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  One slot row                                                       */
/* ------------------------------------------------------------------ */
const SlotRow = ({ slot, onWireNow }) => {
  const status = slot.suggestion_status;
  const isComplete = status === "completed";
  const isFailed = status === "failed";
  const isPending = status === "pending";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 14px",
        borderBottom: "1px solid #f1f5f9",
      }}
    >
      <div style={{ paddingTop: 2 }}>
        {isComplete && <FaCheckCircle size={16} color="#16a34a" />}
        {isPending && <FaHourglassHalf size={16} color="#94a3b8" />}
        {isFailed && <FaExclamationTriangle size={16} color="#dc2626" />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {isComplete && (
          <>
            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>
              {slot.display_name || slot.intent_key}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", fontFamily: "monospace" }}>
              {slot.intent_key}
            </div>
            {slot.description && (
              <div style={{ fontSize: 13, color: "#334155", marginTop: 4 }}>{slot.description}</div>
            )}
            {Array.isArray(slot.examples) && slot.examples.length > 0 && (
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                <strong>Examples:</strong> {slot.examples.slice(0, 3).join(" · ")}
                {slot.examples.length > 3 ? ` (+${slot.examples.length - 3})` : ""}
              </div>
            )}
          </>
        )}
        {isPending && (
          <div style={{ fontSize: 13, color: "#94a3b8" }}>Generating…</div>
        )}
        {isFailed && (
          <>
            <div style={{ fontSize: 13, color: "#b91c1c" }}>
              No suggestion for this slot
            </div>
            {slot.suggestion_error && (
              <div style={{ fontSize: 12, color: "#dc2626", marginTop: 2 }}>
                {slot.suggestion_error}
              </div>
            )}
          </>
        )}
      </div>
      {isComplete && typeof onWireNow === "function" && (
        <button
          type="button"
          onClick={() => onWireNow(slot.intent_key)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 10px",
            background: "#ecfdf5",
            border: "1px solid #86efac",
            color: "#166534",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          Wire now <FaArrowRight size={10} />
        </button>
      )}
    </div>
  );
};

export default NewCustomSkillModal;